import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Severity, SnackbarOptions } from 'hooks/Snackbar';

// Material UI Components
import MaterialSnackbar, { SnackbarProps as MuiSnackbarProps } from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { SpaceBetweenRowBox } from 'components/layout/FlexBox';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import SuccessIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

// Project components
import { BoldTypography } from 'components/display/ColoredText';

const severityStyles: Record<Severity, { color: string; background: string }> = {
  error: { color: '#611a15', background: '#fdecea' },
  warning: { color: '#663c00', background: '#fff4e5' },
  info: { color: '#0d3c61', background: '#e8f4fd' },
  success: { color: '#1e4620', background: '#edf7ed' },
};

export type SnackbarProps = MuiSnackbarProps & {
  title: string;
  options?: SnackbarOptions;
  onClose: () => void;
};

const Snackbar = ({ open, onClose, title, options = { severity: 'info' } }: SnackbarProps) => {
  const { t } = useTranslation(['common']);
  const [expanded, setExpanded] = useState(false);

  const closeSnack = () => {
    setExpanded(false);
    onClose();
  };
  const getSeverityIcon = (severity: Severity) => {
    switch (severity) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      case 'success':
        return <SuccessIcon />;
      default:
    }
  };

  const severitySx = options.severity
    ? {
        color: severityStyles[options.severity].color,
        background: severityStyles[options.severity].background,
        '& button': { color: severityStyles[options.severity].color },
      }
    : {};

  return (
    <MaterialSnackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={options.length || 6000}
      onClose={(_, reason) => {
        if (!(options.closeOnClick === false && reason === 'clickaway')) {
          closeSnack();
        }
      }}
      open={open}
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          bottom: 75,
          left: theme.spacing(1),
          right: theme.spacing(1),
          transform: 'none',
        },
      })}>
      <Card
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          flexGrow: 1,
          backgroundColor: '#e6e6e6',
          [theme.breakpoints.up('md')]: {
            flexGrow: 'initial',
            minWidth: 344,
          },
        })}>
        <CardActions
          sx={{
            p: (theme) => theme.spacing(1, 1, 1, 2),
            ...severitySx,
          }}>
          {options.severity && getSeverityIcon(options.severity)}
          <BoldTypography variant='subtitle2'>{title}</BoldTypography>
          <Box sx={{ ml: 'auto !important' }}>
            {options.body && (
              <IconButton
                aria-label='Show more'
                onClick={() => setExpanded(!expanded)}
                sx={{
                  p: 1,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: '0.3s',
                }}>
                <ExpandMoreIcon />
              </IconButton>
            )}
            <IconButton onClick={closeSnack} sx={{ p: 1, transform: 'rotate(0deg)', transition: '0.3s' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </CardActions>
        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <Paper sx={{ p: 2 }}>
            <SpaceBetweenRowBox>
              <Typography sx={{ my: 'auto' }}>{options.body}</Typography>
              {options.click_action && (
                <Button component={Link} onClick={closeSnack} sx={{ textTransform: 'none' }} to={options.click_action}>
                  {t('COMMON.OPEN')}
                </Button>
              )}
            </SpaceBetweenRowBox>
            {options.image && (
              <Box
                alt={title}
                component='img'
                src={options.image}
                sx={{
                  maxHeight: 250,
                  width: 'auto',
                  objectFit: 'contain',
                  mt: 2,
                  borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                }}
                width='100%'
              />
            )}
          </Paper>
        </Collapse>
      </Card>
    </MaterialSnackbar>
  );
};

export default Snackbar;
